// src/features/quoteItems/quoteItemService.js
import { client } from "../../../api/client";

/**
 * Helpers
 */
const isLocal = () => process.env.REACT_APP_USELOCALDATA === "true";
const baseUri = process.env.REACT_APP_GATEWAY_URI;
const apiPath = "iquoteapi/quoteItem"; // maps to Route("api/[controller]") => "api/quoteItem"

/**
 * Fetch all quote items (client-side filter by quoteRevisionId in local mode).
 * Local mode expects ./_local/_quoteItems.json with { quoteItems: [...] }.
 * Returns: QuoteItem[]
 */
export const fetchQuoteItems = async (quoteRevisionId) => {
    if (isLocal()) {
        try {
            let all = require("./_local/_quoteItems.json").quoteItems || [];
            if (quoteRevisionId != null) {
                all = all.filter(r => r.quoteRevisionId === quoteRevisionId);
            }
            return all;
        } catch (error) {
            console.error("Error fetching quote items (local):", error);
            return [];
        }
    } else {
        try {
            // Controller returns full list (DeleteDate == null); paginate/filter client-side for now
            const response = await client.get(baseUri, `${apiPath}/ByQuoteRevisionId/${quoteRevisionId}`);
            let all = Array.isArray(response.data) ? response.data : [];

            // Convert JSON fields to objects/arrays
            all = all.map(x => ({
                ...x,
                tags: JSON.parse(x.tags),
                customAttributes: JSON.parse(x.customAttributes),
                typeCustomAttributes: JSON.parse(x.typeCustomAttributes)
            }))

            return all
        } catch (error) {
            console.error("Error fetching quote items:", error);
            return [];
        }
    }
};

/**
 * Create a quote item
 * Controller: POST api/quoteItem (accepts QuoteItemDTO)
 * Returns: server response (201) or a mock with newId in local
 */
export const createQuoteItem = async (quoteItem) => {
    if (isLocal()) {
        return { ...quoteItem, id: Math.floor(Math.random() * 100000) };
    } else {
        try {
            var body = structuredClone(quoteItem);

            /*
                Convert fields that need to be provided in JSON format
            */
            body.tags = JSON.stringify(body.tags, null, 2)
            body.customAttributes = JSON.stringify(body.customAttributes, null, 2)
            body.typeCustomAttributes = JSON.stringify(body.typeCustomAttributes, null, 2)

            // Store original upfront and recurring names for later comparison when determining if user has overridden it or not,
            // so we know whether to update it with new module value or leave as user override on quote revisions
            body.originalUpfrontNameOnQuote = quoteItem.upfrontNameOnQuote; 
            body.originalRecurringNameOnQuote = quoteItem.recurringNameOnQuote;

            /*
                Save to DB
            */

            var result = await client.post(baseUri, `${apiPath}`, body);

            if (result.status !== 201) { //  201 = Created
                console.error("Error creating quote item, status:", result.status);
                return undefined;
            }

            console.log(result);
            var savedQuoteItem = result.data.entity;

            // Convert JSON fields to objects/arrays
            savedQuoteItem.tags = JSON.parse(savedQuoteItem.tags)
            savedQuoteItem.customAttributes = JSON.parse(savedQuoteItem.customAttributes)
            savedQuoteItem.typeCustomAttributes = JSON.parse(savedQuoteItem.typeCustomAttributes)

            return savedQuoteItem;
        } catch (error) {
            console.error("Error creating quote item:", error);
            return false;
        }
    }
};

/**
 * Bulk Create quote items
 * Controller: POST api/quoteItem/bulk (accepts QuoteItemDTO array)
 * Returns: server response (201) or a mock with newId in local
 */
export const createQuoteItems = async (quoteItems) => {
    if (isLocal()) {
        return quoteItems.map(item => ({ ...item, id: Math.floor(Math.random() * 100000) }));
    } else {
        try {
            var body = structuredClone(quoteItems);

            body = body.map(item => (
                {
                    ... item,

                    // Convert fields that need to be provided in JSON format  
                    tags: JSON.stringify(item.tags, null, 2),
                    customAttributes: JSON.stringify(item.customAttributes, null, 2),
                    typeCustomAttributes: JSON.stringify(item.typeCustomAttributes, null, 2),

                    // Store original upfront and recurring names for later comparison when determining if user has 
                    // overridden it or not, so we know whether to update it with new module value or leave as user
                    // override on quote revisions
                    originalUpfrontNameOnQuote: item.upfrontNameOnQuote,
                    originalRecurringNameOnQuote: item.recurringNameOnQuote
                }
            ))

            /*
                Save to DB
            */

            var response = await client.post(baseUri, `${apiPath}/bulk`, body);

            if (response.status !== 201) { //  201 = Created
                console.error("Error creating quote items, status:", response.status);
                return undefined;
            }

            var savedQuoteItems = response.data.result;

            const quoteItemsMap = new Map(quoteItems.map(item => [item.guid, item]));

            savedQuoteItems = savedQuoteItems.map(savedQuoteItem => {
                const itemSentToBeSaved = quoteItemsMap.get(savedQuoteItem.guid);
                return ({
                        ...itemSentToBeSaved,
                        id: savedQuoteItem.id,

                        // This is already saved to the DB, as we added it to the body above, but we need to add it back to the,
                        // returned object for later use in the app
                        originalUpfrontNameOnQuote: itemSentToBeSaved.upfrontNameOnQuote,
                        originalRecurringNameOnQuote: itemSentToBeSaved.recurringNameOnQuote
                    })
                }
            )

            console.log("Saved quote items:", savedQuoteItems);

            return savedQuoteItems;        
        } catch (error) {
            console.error("Error creating quote items:", error);
            return undefined;
        }                      
    }
};

/**
 * Bulk update quote items
 * Controller: POST api/quoteItem/bulk (accepts QuoteItemDTO array)
 * Returns: server response (201) or a mock with newId in local
 */
export const updateQuoteItems = async (quoteItems) => {
    if (isLocal()) {
        return quoteItems.map(item => ({ ...item, id: Math.floor(Math.random() * 100000) }));
    } else {
        try {
            var body = structuredClone(quoteItems);

            body = body.map(item => (
                /*
                    Convert fields that need to be provided in JSON format  
                */
                {
                    ... item,
                    tags: JSON.stringify(item.tags, null, 2),
                    customAttributes: JSON.stringify(item.customAttributes, null, 2),
                    typeCustomAttributes: JSON.stringify(item.typeCustomAttributes, null, 2)
                }
            ))

            /*
                Save to DB
            */

            var response = await client.put(baseUri, `${apiPath}/bulk`, body);

            if (response.status !== 204) { //  204 = No content, indicates success for update
                console.error("Error updating quote items, status:", response.status);
                return false;
            } else {
                console.log("Saved quote items:", quoteItems);

                return response.status === 204; //  No content = success
            }

        } catch (error) {
            console.error("Error updating quote items:", error);
            return undefined;
        }                      
    }
};

/**
 * Update a quote item
 * Controller: PUT api/quoteItem/{id} (accepts QuoteItemDTO)
 */
export const updateQuoteItem = async (quoteItem) => {
    if (isLocal()) {
        return { data: { success: true } };
    } else {
        var body = structuredClone(quoteItem);

        /*
            Convert fields that need to be provided in JSON format
        */
        body.tags = JSON.stringify(body.tags, null, 2)
        body.customAttributes = JSON.stringify(body.customAttributes, null, 2)
        body.typeCustomAttributes = JSON.stringify(body.typeCustomAttributes, null, 2)

        try {
            var result = await client.put(baseUri, `${apiPath}/${quoteItem.id}`, body);

            return result.status === 204; //  No content = success
        } catch (error) {
            console.error("Error updating quote item:", error);
            return false;
        }
    }
};

/**
 * Delete a quote item
 * Controller: DELETE api/quoteItem/{id}
 */
export const deleteQuoteItem = async (quoteItem) => {
    if (isLocal()) {
        return { data: { success: true } };
    } else {
        try {
            var result = await client.delete(baseUri, `${apiPath}/${quoteItem.id}`);

            return result.status === 204; //  No content = success
        } catch (error) {
            if (error?.response?.status === 404) {
                return { data: { notFound: true } };
            }
            console.error("Error deleting quote item:", error);
            return false;
        }
    }
};

/**
 * Delete quote items
 * Controller: DELETE api/quoteItem/{id}
 */
export const deleteQuoteItems = async (quoteItems) => {
    console.log("deleteQuoteItems called with items:", quoteItems);
    if (isLocal()) {
        return { data: { success: true } };
    } else {
        try {
            var result = await client.delete(baseUri, `${apiPath}`, { body: quoteItems.map(i => i.id) });

            return result.status === 204; //  No content = success
        } catch (error) {
            if (error?.response?.status === 404) {
                return { data: { notFound: true } };
            }
            console.error("Error deleting quote items:", error);
            return false;
        }
    }
};


